import 'url-polyfill';
import VueResource from 'vue-resource';
import Vue from 'vue';
import { event } from 'vue-analytics';

Vue.use(VueResource);

const urlPrefix = `${window.location.origin}${window.SITE_URL}`;

// Helper function to format property ID similar to PHP's getDisplayStockId
function toTitleCase(str) {
  if (!str) {
    return '';
  }
  // This regex finds words and capitalizes the first letter, lowercasing the rest.
  return str.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

function formatPropertyIdForUrl(icsType, id) {
  const idStr = String(id);
  // Ensure icsType is a string and lowercase for consistent comparison
  const type = String(icsType || '').toLowerCase();

  switch (type) {
    case 'i':
      return `I${idStr.padStart(8, '0')}`;
    case 'c':
    case 'o': // Handle 'o' as commercial type based on PHP logic
      return `C${idStr.padStart(7, '0')}`;
    case 's':
      return `S${idStr.padStart(6, '0')}`;
    default:
      return idStr; // Fallback to raw ID if type is unknown
  }
}

export default {
  getStocksByAnchor(params) {
    const query = {};

    Object.keys(params).forEach((filterAttr) => {
      const queryKey = filterAttr.replace(
        /([A-Z])/g,
        m1 => `_${m1.toLowerCase()}`,
      );
      query[queryKey] = params[filterAttr];
    });
    const url = new URL('stock/stockFilterByAnchorJson', urlPrefix);
    return Vue.http
      .get(url.href, {
        params: query,
      })
      .then(res => res.json());
  },

  getStocks(params) {
    const query = {};
    Object.keys(params).forEach((filterAttr) => {
      const queryKey = filterAttr.replace(
        /([A-Z])/g,
        m1 => `_${m1.toLowerCase()}`,
      );
      query[queryKey] = params[filterAttr];
    });
    const url = new URL('stock/stockFilterJson', urlPrefix);
    return Vue.http
      .get(url.href, {
        params: query,
      })
      .then(res => res.json());
  },

  getHistoryStocks(params) {
    const query = {};
    Object.keys(params).forEach((filterAttr) => {
      const queryKey = filterAttr.replace(
        /([A-Z])/g,
        m1 => `_${m1.toLowerCase()}`,
      );
      query[queryKey] = params[filterAttr];
    });
    const url = new URL('stock/StockArchiveFilterJson', urlPrefix);
    return Vue.http
      .get(url.href, {
        params: query,
      })
      .then(res => res.json());
  },

  buyStocks(stocks, cb) {
    setTimeout(() => {
      cb();
    }, 100);
  },

  getDistricts() {
    const url = new URL('api/districts/ics', urlPrefix);
    const query = {};
    return Vue.http
      .get(url.href, {
        params: query,
        before(request) {
          if (Vue.http.previousRequest) {
            Vue.http.previousRequest.abort();
          }
          Vue.http.previousRequest = request;
        },
      })
      .then(res => res.json());
  },

  getStockKeywords(query) {
    const params = {
      ...query,
      limit: 14,
      lang: window.lang,
    };
    const url = new URL('api/stock_keyword', urlPrefix);
    return Vue.http
      .get(url.href, {
        params,
      })
      .then(res => res.json());
  },

  getSuggestedStock() {
    const url = new URL('api/suggested_stock', urlPrefix);
    return Vue.http.get(url.href).then(res => res.json());
  },

  getSuggestedStocks(stockId) {
    const params = {
      stockId,
    };
    const url = new URL('api/suggested_stocks', urlPrefix);
    return Vue.http
      .get(url.href, {
        params,
      })
      .then(res => res.json());
  },

  getOpenday() {
    const url = new URL('api/openday', urlPrefix);
    return Vue.http.get(url.href).then(res => res.json());
  },

  getHomeStock(params) {
    const url = new URL('api/home_stock', urlPrefix);
    return Vue.http.get(url.href, { params }).then(res => res.json());
  },

  getStockId(stock) {
    // for convenience, should create a mixin instead
    let stockId = stock.gstock_id;
    switch (stock.ics_type) {
      case 'i':
        stockId = `I${stockId.padStart(8, '0')}`;
        break;
      case 'c':
        stockId = `C${stockId.padStart(7, '0')}`;
        break;
      case 's':
        stockId = `S${stockId.padStart(6, '0')}`;
        break;
      default:
        break;
    }

    return stockId;
  },

  gotoStock(payload) {
    let ics;
    let gaAction = 'ICS';
    let gaLabel = 'details-ics';
    switch (payload.stock.ics_type) {
      case 'i':
        ics = 'industrial';
        gaAction = 'Industrial';
        gaLabel = 'details-i';
        break;
      case 'c':
        ics = 'office';
        gaAction = 'Commercial';
        gaLabel = 'details-c';
        break;
      case 's':
        ics = 'shops';
        gaAction = 'Shops';
        gaLabel = 'details-s';
        break;
      default:
        break;
    }
    gaLabel = `${gaLabel}-${payload.stock.gstock_id}`;
    event('gotoStockDetail', gaAction, gaLabel, 0);

    const districtName = payload.stock.dist_eng_name.replaceAll(' ', '-');
    const name = payload.stock.eng_name
      ? payload.stock.eng_name.replace(/[^a-zA-Z0-9]/g, '-')
      : '-';
    window.open(
      `${window.SITE_URL}details/${ics}/${districtName}/${name}/${payload.stock.gstock_id}`,
      payload.newTab ? '_blank' : '_self',
    );
  },

  gotoStock2(payload) {
    // --- Start: Keep existing GA event logging logic ---
    let gaAction = 'ICS';
    let gaLabel = 'details-ics';
    if (payload && payload.stock) {
      switch (payload.stock.ics_type) {
        case 'i':
          gaAction = 'Industrial';
          gaLabel = 'details-i';
          break;
        case 'c':
          gaAction = 'Commercial';
          gaLabel = 'details-c';
          break;
        case 's':
          gaAction = 'Shops';
          gaLabel = 'details-s';
          break;
        default:
          break;
      }
      if (payload.stock.gstock_id) {
        gaLabel = `${gaLabel}-${payload.stock.gstock_id}`;
      } else {
        gaLabel = `${gaLabel}-unknownid`;
      }
    } else {
      gaLabel = `${gaLabel}-unknownpayload`;
    }
    event('gotoStockDetail', gaAction, gaLabel, 0);
    // --- End: Keep existing GA event logging logic ---

    const stock = payload && payload.stock;

    if (!stock || !stock.gstock_id || !stock.dist_code) {
      return;
    }

    const rawWindowLang =
      typeof window !== 'undefined' && window.lang
        ? String(window.lang).toLowerCase()
        : 'zh-hk';
    let localeForUrl;

    if (rawWindowLang === 'en' || rawWindowLang === 'english') {
      localeForUrl = 'en';
    } else if (rawWindowLang === 'zh-hk') {
      localeForUrl = 'zh-hk';
    } else if (rawWindowLang === 'zh-cn' || rawWindowLang === 'gb') {
      localeForUrl = 'zh-cn';
    } else {
      localeForUrl = 'zh-hk';
    }

    let propertyNameRaw;
    if (localeForUrl === 'en') {
      propertyNameRaw = stock.eng_name;
    } else if (localeForUrl === 'zh-hk') {
      propertyNameRaw = stock.chi_name;
    } else if (localeForUrl === 'zh-cn') {
      const nameForConversion = stock.chi_name;
      if (
        nameForConversion &&
        typeof window !== 'undefined' &&
        typeof window.convertStr === 'function'
      ) {
        propertyNameRaw = window.convertStr(nameForConversion, 'gb');
      } else if (nameForConversion) {
        propertyNameRaw = nameForConversion;
      } else {
        propertyNameRaw = stock.eng_name;
      }
    }

    if (!propertyNameRaw) {
      propertyNameRaw = stock.eng_name || stock.chi_name || 'property';
    }

    // Sanitize property name for URL
    let tempName;
    if (localeForUrl === 'en') {
      // For English, apply Title Case.
      tempName = toTitleCase(propertyNameRaw);
    } else {
      // For other languages, just lowercase it.
      tempName = propertyNameRaw.toLowerCase();
    }

    tempName = tempName.replace(/[\s/\\()|,.:&@!"'+*?#%=;<>[\],{}^~`$]+/g, '-'); // Replace spaces and common special chars with hyphen
    // Keep EN alphanumeric (case-insensitive), Chinese chars, hyphens.
    tempName = tempName.replace(/[^a-zA-Z0-9\u4e00-\u9fa5-]/g, '');
    tempName = tempName.replace(/-+/g, '-'); // Collapse multiple hyphens
    tempName = tempName.replace(/^-+|-+$/g, ''); // Trim leading/trailing hyphens
    const propertyNameForUrl = tempName || 'na'; // Final fallback if empty after sanitization

    // Sanitize dist_code for URL
    let tempDistCode = String(stock.dist_code || ''); // Ensure it's a string
    tempDistCode = tempDistCode.toLowerCase(); // Sanitize in lowercase first
    tempDistCode = tempDistCode.replace(
      /[\s/\\()|,.:&@!"'+*?#%=;<>[\],{}^~`$]+/g,
      '-',
    );
    // Keep EN alphanumeric (a-z, 0-9) and hyphens.
    // Other characters (including Chinese chars if any were in dist_code) will be removed.
    tempDistCode = tempDistCode.replace(/[^a-z0-9-]/g, '');
    tempDistCode = tempDistCode.replace(/-+/g, '-'); // Collapse multiple hyphens
    tempDistCode = tempDistCode.replace(/^-+|-+$/g, ''); // Trim leading/trailing hyphens

    let distCodeForUrl = tempDistCode;
    if (!distCodeForUrl) {
      distCodeForUrl = 'UNKNOWN-DIST'; // Fallback if empty after sanitization
    } else {
      distCodeForUrl = distCodeForUrl.toUpperCase(); // Convert to uppercase if not fallback
    }

    const propertyId = formatPropertyIdForUrl(stock.ics_type, stock.gstock_id);

    const urlPathSegmentName = `${distCodeForUrl}-${propertyNameForUrl}`;
    const newUrl = `/${localeForUrl}/property/${urlPathSegmentName}/${propertyId}`;
    window.open(newUrl, payload.newTab ? '_blank' : '_self');
  },
  formatPropertyIdForUrl,
  txTypeToCollectionServiceMap(txType) {
    switch (txType) {
      case 'B':
        return 'buyrent';
      case 'L':
        return 'rent';
      case 'S':
      default:
        return 'buy';
    }
  },
};
